// src/assistant/runtime/useGraphQLAssistantRuntime.ts

import { useLocalRuntime } from '@assistant-ui/react';
import { MessageType, useHandleNewUserMessageMutation } from 'gql/types';
import { useRef } from 'react';

function flattenContent(content: any): string {
    if (Array.isArray(content)) {
        return content
            .map((part) => (part?.type === 'text' ? part.text : ''))
            .join('\n');
    }
    return typeof content === 'string' ? content : '';
}

export function useGraphQLAssistantRuntime(interactionID?: string | null) {
    const [handleNewUserMessage] = useHandleNewUserMessageMutation();
    const interactionIDRef = useRef(interactionID ?? 'default');

    return useLocalRuntime({
        run: async ({ messages, abortSignal }) => {
            const flattenedMessages = messages.map((m) => ({
                role: m.role,
                content: flattenContent(m.content),
            }));

            const lastUserMessage = flattenedMessages.at(-1);
            if (!lastUserMessage) {
                return { content: [{ type: 'text', text: 'Empty message.' }] };
            }

            const { data } = await handleNewUserMessage({
                variables: {
                    interactionID: interactionIDRef.current,
                    message: { content: lastUserMessage.content },
                },
                context: {
                    fetchOptions: { signal: abortSignal },
                },
            });

            const assistantMessage = data?.handleNewUserMessage?.message;

            if (!assistantMessage) {
                return { content: [{ type: 'text', text: 'No assistant response.' }] };
            }

            return {
                content: [{ type: 'text', text: assistantMessage.content }],
            };
        },
    });
}
